import { useId } from 'react';
import Svg, { Circle, ClipPath, Defs, Path } from 'react-native-svg';

import type { AvatarToken } from './specialistAvatarTone';

import { useColors } from '@/hooks/useColors';

/**
 * The app's no-photo doctor placeholder, `primum_default_avatar`, redrawn as a vector so its two
 * colors can come from the theme, as one of the variants in `specialistAvatarTone.ts`. Traced with potrace from the 570px PNG and checked against it
 * at 3x; the two differ on 0.15% of pixels, all on anti-aliased edges.
 *
 * The figure is clipped to the circle, as in the PNG, where the shoulders run off its edge.
 *
 * Every avatar gets its own clip id. On web the clip is a DOM id, and with one shared id every
 * avatar on the page points at the first one in the document, so when that one sits in a hidden
 * subtree the browser drops the figure from all the others and a header shows an empty circle.
 */
const VIEW_BOX = 570;

const RADIUS = VIEW_BOX / 2;

/** The head and the shoulders with the stethoscope cut out of them, from the trace. */
const FIGURE_PATH =
  'M 268 80.7 C 266.6 80.9 261.3 82.2 256.2 83.5 C 206 96.7 171.4 146.9 177 198.6 C 181.9 244.4 213.1 280.9 257.7 293 C 267.7 295.8 290.7 296.8 301.8 295 C 344.1 288.3 378.4 257.1 389.8 215.2 C 392.1 206.4 392.4 203.8 392.4 188 C 392.4 172.3 392.1 169.5 389.7 160.8 C 378.8 120.1 345.9 89.3 305 81.6 C 297.4 80.1 274.6 79.6 268 80.7 M 197 291.7 C 194.3 292.7 177.1 303.2 176.2 304.3 C 174.5 306.6 169.9 319.3 167.5 328.5 C 162.4 348.4 162.1 367.2 166.8 382.7 L 168.6 388.5 175.3 389.2 C 197.6 391.4 207.3 415.1 193.5 433.5 C 190.4 437.7 182 442 175.7 442.7 C 155.9 444.7 140.8 424.8 147.8 406.2 L 149.7 401.3 146.4 391.4 C 140.4 373.5 139.6 353.2 144 332 C 145.1 326.8 145.8 322.5 145.7 322.4 C 145.3 322 119.2 339.7 110.7 346.2 C 99.6 354.7 87.7 366.2 82.5 373.5 C 71.6 388.9 64.8 409.7 61.5 437.8 C 59 459.2 58.8 458.2 67.9 469.1 C 72.2 474.3 81.1 483.7 87.6 490 C 175 574.8 307.5 594.2 415.7 538 C 442.4 524.2 464.6 507.7 487 485.1 C 499.5 472.5 513.6 456.2 510.4 458.3 C 509.6 458.7 509.1 455.8 508.6 449.2 C 506.4 418 499.4 391.3 489.4 376 C 482 364.8 462.7 348.2 436.1 330.1 C 428.7 325.1 422.6 321.1 422.5 321.3 C 422.4 321.4 422.5 325.6 422.8 330.5 L 423.2 339.5 428.4 341.8 C 446.3 349.7 459.7 371.1 465.7 401.5 C 468.1 413.5 468.1 455.1 465.7 471.5 C 464.3 481.6 463.9 482.7 461 485.3 L 458 488 439.6 488 C 422.7 488 421.1 487.8 418.7 486 C 415.1 483.1 413.5 477.9 415 473.5 C 417.1 467.1 419.7 466 432.5 466 L 443.8 466 444.5 454.3 C 446.4 424.1 442.3 393.5 434.3 377.6 C 423.9 357.1 400.1 353 384 369.1 C 371.1 382 360.2 417.5 359.7 448.5 L 359.5 457.4 368.5 459 C 379.3 461 381.3 462 383 466 C 385.7 472.6 379.6 482 372.7 482 C 368.1 482 343.6 477.4 341.1 476.1 C 335.8 473.2 335 466.8 337.5 443.8 C 339.9 420.7 345.7 395.7 352 380.7 C 361.7 357.5 378 341.6 395.7 338 L 401.3 336.8 400.7 330 C 400.1 322.2 397.4 310.8 395.4 306.8 C 394.4 304.9 390.4 302.2 382.3 297.8 C 375.8 294.3 370.3 291.4 369.9 291.2 C 369.5 291 367.1 294.6 364.5 299.2 C 350.2 324.2 333.5 337.7 307.4 345.2 C 296 348.5 273 348.5 261.6 345.2 C 236.1 337.9 220.4 325.2 205.1 299.8 C 202.2 294.9 199.6 291 199.2 291.1 C 198.8 291.1 197.8 291.4 197 291.7';

function resolve(colors: Record<string, Record<string, string>>, token: string): string {
  const [group, shade] = token.split('-');

  return colors[group][shade];
}

export function SpecialistFigure({
  circle,
  figure,
}: {
  /** The brand token the circle is filled with. */
  circle: AvatarToken;
  /** The brand token of the same group the figure is drawn in. */
  figure: AvatarToken;
}) {
  const { colors } = useColors();

  const palette = colors as unknown as Record<string, Record<string, string>>;

  // React's ids carry colons, which a `url(#...)` reference does not accept.
  const clipId = `specialist-figure-clip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <Svg width="100%" height="100%" viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`}>
      <Defs>
        <ClipPath id={clipId}>
          <Circle cx={RADIUS} cy={RADIUS} r={RADIUS} />
        </ClipPath>
      </Defs>
      <Circle cx={RADIUS} cy={RADIUS} r={RADIUS} fill={resolve(palette, circle)} />
      <Path
        d={FIGURE_PATH}
        fill={resolve(palette, figure)}
        fillRule="evenodd"
        clipPath={`url(#${clipId})`}
      />
    </Svg>
  );
}
